"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, Phone } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ConsultationCallToAction() {
  const {
    ref: containerRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section id="consultationcalltoaction" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div ref={containerRef} style={{
        opacity: isVisible ? 1 : 0
      }} className="transition-all duration-700 ease-out translate-y-0 relative rounded-2xl border border-border bg-card text-card-foreground shadow-xl overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-stretch">
            {/* Left Content Area */}
            <div className="lg:col-span-7 p-8 sm:p-12 lg:p-16 flex flex-col justify-center">
              <span className="text-xs uppercase tracking-widest text-primary font-semibold mb-3">
                Brookhaven, GA Remodeling
              </span>

              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-foreground leading-tight mb-6">
                Ready to elevate your living space?
              </h2>

              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mb-8 max-w-xl">
                From luxury kitchen updates and master bath retreats to whole-home additions, our Brookhaven team brings exceptional craft and dedicated management to every project.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8 text-sm text-foreground/90">
                <div className="flex items-center gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>Transparent architectural estimates</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>Experienced Georgia craftsmen</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>Dedicated on-site project lead</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>Detailed milestone scheduling</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-medium text-base h-12 px-8 group" asChild>
                  <Link to="/ContactRequestAnEstimatePage">
                    <span>Get a Free Estimate</span>
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>

                <div className="flex items-center gap-3 px-3 py-2 text-muted-foreground text-sm">
                  <div className="p-2 rounded-full bg-muted text-foreground">
                    <Phone className="size-4" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Prefer to call?</p>
                    <p className="font-medium text-foreground">Direct Consultation</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Property Image Area */}
            <div className="lg:col-span-5 relative min-h-[320px] lg:min-h-full overflow-hidden bg-muted">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_residential_renovation_exterior.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_residential_renovation_exterior.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_residential_renovation_exterior.png-wvc-sizes" alt="Renovated Craftsman residence in Brookhaven, Georgia neighborhood" loading="lazy" decoding="async" className="w-full h-full object-cover object-center absolute inset-0 transition-transform duration-700 hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent lg:hidden" />
              <div className="absolute bottom-4 left-4 right-4 text-white text-xs lg:hidden">
                <p className="font-medium">Brookhaven Neighborhood Transformation</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}